<script setup>
import i18n from '../../plugins/i18n'
const { t } = i18n.global
const { mobile } = useDisplay()
import { computed } from 'vue'
import { humanReadableDuration, humanReadableTimestamp } from '@/composables/datetime'
import { useDisplay } from 'vuetify'

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const humanReadableTime = computed(() => {
  return `${humanReadableTimestamp(props.incident.firstEventTimestamp)} - ${humanReadableTimestamp(props.incident.lastEventTimestamp)}`
})
const wasCreatedAutomatically = computed(() => {
  return props.incident.triggeredBy.entityType !== 'account'
})
const age = computed(() => {
  if (props.incident.state === 'closed') return props.incident.lastEventTimestamp - props.incident.firstEventTimestamp
  return Date.now() - props.incident.firstEventTimestamp
})

const ageColor = computed(() => {
  if (age.value >= 1000 * 60 * 60 * 24 * 2) return 'red'
  if (age.value >= 1000 * 60 * 60 * 24) return 'orange'
  return 'green'
})

const statusChipText = computed(() => {
  return t('incidents.deadAnimal.status.' + props.incident.state)
})

const statusIcon = computed(() => {
  if (props.incident.state === 'closed') {
    return 'mdi-lock'
  }
  if (props.incident.state === 'open') {
    return 'mdi-lock-open-variant'
  }
  if (props.incident.state === 'deadAnimalExists') {
    return 'mdi-skull-crossbones'
  }
  return 'mdi-information'
})
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-start flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-skull-crossbones"
            start
          />
          {{ $t('incidents.deadAnimal.title') }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            variant="flat"
            :color="props.incident.state === 'deadAnimalExists' ? 'primary' : 'default'"
          >
            {{ statusChipText }}
          </v-chip>
          <v-chip
            v-if="incident.messagePayload.falsePositive"
            class="px-4"
            prepend-icon="mdi-cube-scan"
            size="small"
            color="error"
          >
            {{ $t('incidents.deadAnimal.titles.false_positive') }}
          </v-chip>
          <v-chip
            v-if="incident.state !== 'closed' && incident.isPublicOpen === false"
            class="px-4"
            prepend-icon="mdi-eye-off"
            size="small"
            color="warning"
          >
            {{ $t('incidents.titles.public_closed') }}
          </v-chip>
          <v-chip
            v-if="wasCreatedAutomatically"
            class="px-4"
            prepend-icon="mdi-robot"
            size="small"
          >
            {{ $t('incidents.deadAnimal.properties.created_automatically') }}
          </v-chip>
        </div>
      </div>

      <div class="d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>
    <v-card-text class="py-2">
      <v-list-item
        v-if="mobile"
        prepend-icon="mdi-list-status"
        :title="$t('incidents.deadAnimal.titles.status')"
        :subtitle="statusChipText"
      />
      <v-list-item
        v-if="incident.messagePayload.falsePositive"
        prepend-icon="mdi-cube-scan"
        :title="$t('incidents.deadAnimal.titles.false_positive')"
        :subtitle="$t('incidents.deadAnimal.properties.false_positive')"
      />
      <v-list-item
        v-if="incident.state !== 'closed' && incident.isPublicOpen === false"
        prepend-icon="mdi-eye-off"
        :title="$t('incidents.titles.public_closed')"
        :subtitle="$t('incidents.properties.public_closed')"
      />
      <v-list-item
        v-if="incident.state !== 'closed'"
        prepend-icon="mdi-clock"
        :title="$t('incidents.deadAnimal.titles.firstDetection')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
      />
      <v-list-item
        v-if="incident.state === 'closed'"
        prepend-icon="mdi-clock"
        :title="$t('incidents.deadAnimal.titles.time')"
        :subtitle="humanReadableTime"
      />
      <v-list-item
        :title="$t('incidents.deadAnimal.titles.age')"
        prepend-icon="mdi-timelapse"
      >
        <template #subtitle>
          <v-chip
            :color="ageColor"
            variant="flat"
          >
            {{ humanReadableDuration(age) }}
          </v-chip>
        </template>
      </v-list-item>
      <v-list-item
        :title="$t('incidents.deadAnimal.titles.camera')"
        :subtitle="incident.messagePayload.cameraName"
        prepend-icon="mdi-video"
      />
      <v-list-item
        v-if="!wasCreatedAutomatically"
        :title="$t('incidents.deadAnimal.titles.creator')"
        :subtitle="incident.triggeredBy.displayName"
        prepend-icon="mdi-account"
      />
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot
        name="genericActions"
        v-bind="{
          enableNewEventButton: incident.state !== 'closed',
          newEventButtonText: $t('incidents.deadAnimal.actions.new_event'),
        }"
      />
    </v-card-actions>
  </v-layout-card>
</template>
